import { ChevronRight, Mail, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Avatar, Button, Card, PageHeader } from '../components/ui';
import { legislatorsAPI } from '../lib/api';
import { useProfile } from '../hooks/useCitizenData';
import { initialsOf, partyColor } from '../lib/utils';

interface Rep {
  id: string;
  fullName: string;
  chamber: string;
  district: string;
  partyAffiliation?: string | null;
  email?: string | null;
}

// "Calls to action" reimagined as a real contact-your-representatives screen:
// it surfaces the citizen's sitting members (by district) with direct email,
// rather than the prototype's mock compose/share/track flow.
export default function ContactRepsScreen() {
  const navigate = useNavigate();
  const { data: profile } = useProfile();

  const { data: reps, isLoading } = useQuery<Rep[]>(
    ['citizen-reps', profile?.houseDistrict, profile?.senateDistrict],
    async () => {
      const out: Rep[] = [];
      const lookups: Array<{ chamber: 'House' | 'Senate'; district: string }> = [];
      if (profile?.houseDistrict) lookups.push({ chamber: 'House', district: profile.houseDistrict });
      if (profile?.senateDistrict) lookups.push({ chamber: 'Senate', district: profile.senateDistrict });
      await Promise.all(
        lookups.map(async (l) => {
          try {
            const { data } = await legislatorsAPI.getByDistrict(l.chamber, l.district);
            out.push(...((data.data?.legislators || []) as Rep[]));
          } catch {
            /* skip */
          }
        })
      );
      return out;
    },
    { enabled: !!(profile?.houseDistrict || profile?.senateDistrict) }
  );

  const hasDistrict = !!(profile?.houseDistrict || profile?.senateDistrict);

  return (
    <div className="flex flex-col screen-enter">
      <PageHeader title="Contact your reps" onBack={() => navigate('/citizen/home')} />

      <div className="px-6 pt-2 pb-24">
        <p className="text-sm text-[color:var(--muted-foreground)] leading-relaxed mb-5">
          Your voice carries weight. Reach the legislators who represent you directly.
        </p>

        {!hasDistrict && (
          <button
            onClick={() => navigate('/citizen/profile')}
            className="w-full rounded-2xl p-4 text-left border hover-elevate"
            style={{ background: 'var(--accent)', borderColor: 'transparent' }}
          >
            <p className="text-sm font-bold flex items-center gap-2" style={{ color: 'var(--primary)' }}>
              <MapPin className="w-4 h-4" /> Add your address
            </p>
            <p className="text-xs text-[color:var(--muted-foreground)] mt-1">
              We'll find the House and Senate members who represent you.
            </p>
          </button>
        )}

        {isLoading && <p className="text-sm text-[color:var(--muted-foreground)] py-6">Finding your representatives…</p>}

        {hasDistrict && !isLoading && (reps?.length || 0) === 0 && (
          <p className="text-sm text-[color:var(--muted-foreground)] py-6">
            We couldn't match representatives to your districts yet.
          </p>
        )}

        <div className="space-y-3">
          {(reps || []).map((r) => (
            <Card key={r.id} className="p-4 border-0">
              <div className="flex items-center gap-3">
                <Avatar initials={initialsOf(r.fullName)} size={44} tone="accent" />
                <button onClick={() => navigate(`/citizen/legislator/${r.id}`)} className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-bold">{r.fullName}</p>
                  <p className="text-xs text-[color:var(--muted-foreground)] flex items-center gap-1.5 mt-0.5">
                    {r.partyAffiliation && (
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: partyColor(r.partyAffiliation) }} />
                    )}
                    {r.chamber} · District {r.district}
                  </p>
                </button>
                <ChevronRight className="w-4 h-4 text-[color:var(--muted-foreground)] shrink-0" />
              </div>
              {r.email && (
                <a href={`mailto:${r.email}`} className="block mt-3">
                  <Button variant="outline" size="sm" className="w-full rounded-xl">
                    <Mail className="w-4 h-4" /> Email {r.fullName.split(' ')[0]}
                  </Button>
                </a>
              )}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
